import { getTranslation } from '@/lib/utils/i18n';

// Request/response plumbing for the REST list endpoints behind queryHandlerMiddleware.

// The middleware reads `page` 0-indexed and parses `sort`/`search`/`filters` as JSON.
const ZERO_BASED = 0;
const ONE_BASED = 1;
export const PAGE_BASES = Object.freeze({ zeroBased: ZERO_BASED, oneBased: ONE_BASED });

/**
 * Query params for a list request.
 *
 * @param state      { page, pageSize, sortBy, sortDir, search } from useTableState
 * @param sortFields column id → the orderable server column; omit to disable sorting
 * @param searchIn   server columns the one search box maps onto; omit to disable
 * @param filterFields state key → server column for the JSON `filters` param
 * @param pageBase   0 for queryHandlerMiddleware routes, 1 for hand-rolled ones
 */
export const buildListQueryParams = (
  state = {},
  { sortFields, searchIn, filterFields, pageBase = PAGE_BASES.zeroBased } = {},
) => {
  const { page = 1, pageSize = 10, sortBy, sortDir, search } = state;
  const params = { page: pageBase === PAGE_BASES.zeroBased ? page - 1 : page, limit: pageSize };

  const sortField = sortFields?.[sortBy];
  if (sortField) params.sort = JSON.stringify({ field: [sortField], sort: sortDir ?? 'asc' });

  const term = search?.trim();
  if (term && searchIn?.length) {
    params.search = JSON.stringify(Object.fromEntries(searchIn.map((field) => [field, term])));
  }

  const filters = Object.entries(filterFields ?? {})
    .map(([key, column]) => [column, state[key]])
    .filter(([, value]) =>
      Array.isArray(value) ? value.length > 0 : value != null && value !== '',
    );
  if (filters.length) params.filters = JSON.stringify(Object.fromEntries(filters));

  return params;
};

// Validation failures carry `{ status, code, output }`; a raw 500 has no `output`.
export const processServerError = (response) => ({
  message: response.data?.output ?? response.data?.message ?? getTranslation('ERROR_GENERIC'),
});

/** Row list + total from a `{ output: { <rowsKey>, total } }` envelope. */
export const processListPage = (dto, rowsKey, processRow) => ({
  rows: (dto?.output?.[rowsKey] ?? []).map(processRow),
  totalCount: dto?.output?.total ?? dto?.output?.pagination?.total ?? 0,
});

// For endpoints whose screen shows the failure itself — notifyApiErrors skips these.
export const handledError = (response) => ({ ...processServerError(response), handled: true });
